import { useState, useEffect } from 'react';
import { useSalesStore } from '../../../store/salesStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Search, DollarSign, Target, Activity } from 'lucide-react';
import { Input } from '../../../components/Input';
import toast from 'react-hot-toast';

export function SalesCRM() {
    const { opportunities, isLoading, fetchOpportunities, createOpportunity, updateOpportunity } = useSalesStore();
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState<'list' | 'pipeline'>('list');

    useEffect(() => {
        fetchOpportunities();
    }, []);

    const filteredOpps = opportunities.filter(o => 
        o.title.toLowerCase().includes(search.toLowerCase())
    );

    const stages = ['prospect', 'qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

    const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

    const handleCreateMock = async () => {
        try {
            await createOpportunity({
                title: 'New Enterprise Deal',
                amount: Math.floor(Math.random() * 50000) + 10000,
                stage: 'prospect',
                probability: 20
            });
            toast.success('Opportunity created');
        } catch (e) {
            toast.error('Failed to create');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Sales CRM</h1>
                    <p className="text-muted-foreground">Manage your sales pipeline and opportunities.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setViewMode(viewMode === 'list' ? 'pipeline' : 'list')}>
                        {viewMode === 'list' ? 'Pipeline View' : 'List View'}
                    </Button>
                    <Button variant="primary" onClick={handleCreateMock}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Opportunity
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <Target className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Active Opps</p>
                            <h3 className="text-foreground font-display text-metric">
                                {opportunities.filter(o => !o.stage.includes('closed')).length}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
                            <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Pipeline Value</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(opportunities.reduce((acc, o) => !o.stage.includes('closed') ? acc + o.amount : acc, 0))}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <Activity className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-muted-foreground">Won Value</p>
                            <h3 className="text-foreground font-display text-metric">
                                {formatCurrency(opportunities.reduce((acc, o) => o.stage === 'closed_won' ? acc + o.amount : acc, 0))}
                            </h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h3 className="text-foreground font-display text-section-title">Opportunities</h3>
                        <div className="relative w-64">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input 
                                placeholder="Search opportunities..." 
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : viewMode === 'list' ? (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                                <tr>
                                    <th className="px-6 py-3">Title</th>
                                    <th className="px-6 py-3">Amount</th>
                                    <th className="px-6 py-3">Stage</th>
                                    <th className="px-6 py-3">Probability</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredOpps.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                                            No opportunities found. Click "Add Opportunity" to create one.
                                        </td>
                                    </tr>
                                ) : filteredOpps.map(op => (
                                    <tr key={op.id} className="hover:bg-secondary/20 transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">{op.title}</td>
                                        <td className="px-6 py-4">{formatCurrency(op.amount)}</td>
                                        <td className="px-6 py-4">
                                            <Badge variant={op.stage.includes('closed') ? 'default' : 'primary'}>
                                                {op.stage.replace('_', ' ').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">{op.probability}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex gap-4 p-4 overflow-x-auto">
                            {stages.map(stage => (
                                <div key={stage} className="flex-1 min-w-[250px] bg-secondary/30 rounded-lg p-3">
                                    <h4 className="text-muted-foreground mb-3 text-sm font-semibold">
                                        {stage.replace('_', ' ')}
                                    </h4>
                                    <div className="space-y-3">
                                        {filteredOpps.filter(o => o.stage === stage).map(op => (
                                            <Card key={op.id}>
                                                <CardContent className="p-3">
                                                    <div className="font-medium text-sm mb-1 text-foreground">{op.title}</div>
                                                    <div className="flex justify-between items-center mt-2">
                                                        <span className="text-xs text-muted-foreground">{formatCurrency(op.amount)}</span>
                                                        <Badge variant="secondary">{op.probability}%</Badge>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
